import { T } from '@/i18n/LocaleProvider';
import Box from '@mui/material/Box';
import { MONO_FONT } from '@/theme';
import Typography from '@mui/material/Typography';

export default function StepFlow({
  steps = [],
  dark = false,
  orientation = 'horizontal',
  showNumber = true,
  breakAt = 'md',
}) {
  const isHorizontal = orientation === 'horizontal';

  return (
    <Box
      sx={
        isHorizontal
          ? {
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                [breakAt]: `repeat(${steps.length}, 1fr)`,
              },
              gap: 0,
            }
          : {
              display: 'flex',
              flexDirection: 'column',
              gap: 0,
            }
      }
    >
      {steps.map((step, i) => (
        <Box
          key={i}
          sx={
            isHorizontal
              ? {
                  position: 'relative',
                  pl: { xs: 0, [breakAt]: i === 0 ? 0 : '24px' },
                  pr: { [breakAt]: i < steps.length - 1 ? '24px' : 0 },
                  borderRight: {
                    [breakAt]:
                      i < steps.length - 1
                        ? `1px solid ${dark ? 'var(--mui-palette-grey-800, #1c1c1c)' : 'var(--mui-palette-grey-200, #e5e5e5)'}`
                        : 'none',
                  },
                  borderBottom: {
                    xs:
                      i < steps.length - 1
                        ? `1px solid ${dark ? '#1f2422' : '#e2e5e3'}`
                        : 'none',
                    [breakAt]: 'none',
                  },
                  pb: { xs: i < steps.length - 1 ? '28px' : 0, [breakAt]: 0 },
                  mb: { xs: i < steps.length - 1 ? '28px' : 0, [breakAt]: 0 },
                }
              : {
                  display: 'flex',
                  gap: '24px',
                  position: 'relative',
                  pb: i < steps.length - 1 ? '36px' : 0,
                  '&::before':
                    i < steps.length - 1
                      ? {
                          content: '""',
                          position: 'absolute',
                          left: '19px',
                          top: '40px',
                          bottom: 0,
                          width: '1px',
                          bgcolor: dark ? 'grey.800' : 'grey.200',
                        }
                      : {},
                }
          }
        >
          {showNumber && (
            <Box
              sx={
                isHorizontal
                  ? {}
                  : {
                      flexShrink: 0,
                      width: '40px',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                    }
              }
            >
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '40px',
                  height: '40px',
                  border: `1px solid ${dark ? '#7ed7c5' : '#d6efe9'}`,
                  fontFamily: MONO_FONT,
                  fontSize: '12px',
                  fontWeight: 500,
                  color: dark ? '#7ed7c5' : 'primary.darkest',
                  mb: isHorizontal ? 3 : 0,
                  flexShrink: 0,
                  backgroundColor: dark
                    ? 'rgba(126,215,197,0.1)'
                    : 'primary.light',
                  borderRadius: 1,
                }}
              >
                {String(i + 1).padStart(2, '0')}
              </Box>
            </Box>
          )}

          <Box sx={isHorizontal ? {} : { pt: '8px' }}>
            {step.label && (
              <Typography
                sx={{
                  fontFamily: MONO_FONT,
                  fontSize: '10px',
                  fontWeight: 600,
                  letterSpacing: '0.12em',
                  textTransform: 'uppercase',
                  color: dark ? '#7ed7c5' : 'text.accent',
                  mb: 2.5,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  '&::after': {
                    content: '""',
                    flex: '1 1 0%',
                    maxWidth: '60px',
                    height: '1px',
                    background: `linear-gradient(to right, ${dark ? '#7ed7c5' : '#009F8B'}, transparent)`,
                  },
                }}
              >
                <T>{step.label}</T>
              </Typography>
            )}
            <Typography
              variant="h4"
              component="h3"
              sx={{
                fontSize: 'clamp(17px, 1.4vw, 18px)',
                fontWeight: 500,
                letterSpacing: '-0.01em',
                color: dark ? 'common.white' : 'text.primary',
                mb: 1.5,
              }}
            >
              <T>{step.title}</T>
            </Typography>
            {step.body && (
              <Typography
                sx={{
                  fontSize: '14px',
                  lineHeight: 1.7,
                  color: dark ? 'text.muted' : 'grey.600',
                }}
              >
                <T>{step.body}</T>
              </Typography>
            )}
          </Box>
        </Box>
      ))}
    </Box>
  );
}
